import type { Ref } from 'react'
import { kebabCase } from 'lodash-es'
import Immutable from 'immutable'

import LogoCollection from './LogoCollection'

type Logo = {
  id: string
  title: string
  filename: string
  active: boolean
}

type Props = {
  updatePopoverPosition: VoidFunction
  fieldsetRef: Ref<HTMLFieldSetElement>
}

export default function ShippingProviderSettings({ updatePopoverPosition, fieldsetRef }: Readonly<Props>) {
  return (
    <LogoCollection scope="shippingLogos" updatePopoverPosition={updatePopoverPosition} fieldsetRef={fieldsetRef} />
  )
}
ShippingProviderSettings.defaultValue = ({ shopLocale }) => {
  const [, countryCode] = shopLocale.replace('_', '-').split('-')

  return Immutable.fromJS([...baseLogos, ...(countryLogos[countryCode] || [])])
}

function logoDefault(id: string, title = id, fileExtension = 'svg'): Logo {
  return {
    id,
    title,
    filename: `${kebabCase(id)}-logo.${fileExtension}`,
    active: false,
  }
}

function pngLogoDefault(id: string, title?: string) {
  return logoDefault(id, title, 'png')
}

// Define locale specific shipping provider logos, based on the table on this page in Confluence:
// https://epages.atlassian.net/wiki/spaces/UNTY/pages/543227905/Shipping+provider+logo+-+local+selection
const baseLogos: Logo[] = [
  logoDefault('dhl', 'DHL'),
  logoDefault('dpd'),
  pngLogoDefault('gls', 'GLS'),
  logoDefault('tntExpress', 'TNT Express'),
  logoDefault('ups', 'UPS'),
]

const countryLogos: { [locale: string]: Logo[] } = {
  DE: [logoDefault('hermes', 'Hermes'), pngLogoDefault('keavo')],
  FR: [pngLogoDefault('chronopost'), logoDefault('colissimo'), pngLogoDefault('mondialRelay', 'mondial relay')],
  IT: [
    logoDefault('posteItaliane', 'Poste Italiane'),
    pngLogoDefault('brtCorriereEspresso', 'BRT Corriere Espresso'),
    logoDefault('nexive'),
  ],
  AT: [logoDefault('oesterreichischePost', 'Österreichische Post')],
  CH: [logoDefault('schweizerischePost', 'Die Schweizerische Post')],
  ES: [
    pngLogoDefault('mrw', 'MRW'),
    pngLogoDefault('zeleris', 'Zeleris'),
    pngLogoDefault('correosExpress', 'Correos Express'),
    logoDefault('correos', 'Correos'),
    logoDefault('envialia'),
    logoDefault('seur', 'SEUR'),
  ],
  US: [pngLogoDefault('fedex', 'FedEx'), pngLogoDefault('usps', 'USPS')],
  CA: [pngLogoDefault('fedex', 'FedEx'), logoDefault('canadaPost', 'Canada Post')],
  NL: [logoDefault('postNl', 'PostNL')],
  BE: [logoDefault('bpost')],
  GB: [logoDefault('royalMail', 'Royal Mail')],
}
